import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext({ theme: "system", setTheme: () => {} });

const STORAGE_KEY = "cv-theme";
const VALID_THEMES = ["light", "dark", "system"];

const getSystemPrefersDark = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const applyDarkClass = (shouldBeDark) => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", shouldBeDark);
};

/**
 * Theme provider with three modes: 'light', 'dark', 'system'.
 * Persists to localStorage under 'cv-theme'. The pre-paint bootstrap script
 * (/public/theme-bootstrap.js) handles the initial render; this provider
 * takes over once React hydrates.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState("system");

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (VALID_THEMES.includes(stored)) {
        setThemeState(stored);
      }
    } catch (e) {
      console.warn("[ThemeContext] localStorage read failed:", e.message);
    }
  }, []);

  useEffect(() => {
    const apply = () => {
      const shouldBeDark =
        theme === "dark" || (theme === "system" && getSystemPrefersDark());
      applyDarkClass(shouldBeDark);
    };
    apply();

    if (theme === "system" && typeof window !== "undefined" && window.matchMedia) {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!VALID_THEMES.includes(next)) return;
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      console.warn("[ThemeContext] localStorage write failed:", e.message);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
